import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Crown, Utensils, CreditCard, ToggleRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const AdminPage = () => {
  const { getAuthHeaders, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const togglePremium = async (userId) => {
    try {
      const response = await axios.put(
        `${API}/admin/users/${userId}/premium`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_premium: response.data.is_premium } : u
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-muted-foreground">Cargando panel admin...</div>
        </div>
      </MainLayout>
    );
  }

  const statCards = [
    { 
      icon: <Users className="w-5 h-5" />, 
      label: "Total Usuarios", 
      value: stats?.total_users || 0,
      color: "text-primary"
    },
    { 
      icon: <Crown className="w-5 h-5" />, 
      label: "Usuarios Premium", 
      value: stats?.premium_users || 0,
      color: "text-accent"
    },
    { 
      icon: <Utensils className="w-5 h-5" />, 
      label: "Total Comidas", 
      value: stats?.total_meals || 0,
      color: "text-chart-protein"
    },
    { 
      icon: <CreditCard className="w-5 h-5" />, 
      label: "Pagos Completados", 
      value: stats?.paid_transactions || 0,
      color: "text-chart-carbs"
    }
  ];

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Panel de Administración</h1>
          <p className="text-muted-foreground">Gestión de usuarios y estadísticas</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-card border border-border/50 rounded-2xl p-5"
            >
              <div className={`${stat.color} mb-3`}>{stat.icon}</div>
              <p className="font-mono text-2xl font-bold">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-border/50 rounded-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-border/50">
            <h3 className="font-heading font-semibold">Usuarios registrados</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Usuario</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Email</th>
                  <th className="text-center p-4 text-sm font-medium text-muted-foreground">Comidas</th>
                  <th className="text-center p-4 text-sm font-medium text-muted-foreground">Estado</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr 
                    key={user.id}
                    className="border-t border-border/50 hover:bg-muted/30 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">{user.email}</td>
                    <td className="p-4 text-center">
                      <span className="font-mono">{user.meals_count || 0}</span>
                    </td>
                    <td className="p-4 text-center">
                      {user.is_premium ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent rounded-full text-xs font-medium">
                          <Crown className="w-3 h-3" />
                          Premium
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs font-medium">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePremium(user.id)}
                        className="rounded-lg"
                        data-testid={`toggle-premium-${user.id}`}
                      >
                        <ToggleRight className="w-4 h-4 mr-1" />
                        {user.is_premium ? "Quitar Premium" : "Dar Premium"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {users.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              No hay usuarios registrados
            </div>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default AdminPage;
